import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import {
  ModalStepConfiguration,
  StepConfig,
  StepIds,
  usePartnerStepConfiguration,
} from '~/components/premarketplace/partner-config';
import { MarketplacePartner } from '~/types/marketplace';

/** StepManager
 *  Manages the current step and navigation between steps.
 */
export interface StepManager {
  currentStepId: StepIds;
  currentStepIndex: number;
  totalSteps: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (step: number) => void;
  resetSteps: () => void;
}

const useStepManager = (steps: StepConfig[]): StepManager => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();

  const pathnameStepId = pathname.split('premarketplace/')[1] as StepIds | undefined;
  const [currentStepId, setCurrentStepId] = useState<StepIds>(pathnameStepId ?? steps[0]?.id);

  // Keep state in sync when Remix navigates externally (e.g. index → first step redirect)
  // without going through nextStep()/previousStep().
  useEffect(() => {
    if (pathnameStepId) setCurrentStepId(pathnameStepId);
  }, [pathnameStepId]);

  const currentStepIndex = useMemo(() => {
    const stepIndex = steps?.findIndex((step) => step.id === currentStepId);
    return stepIndex > 0 ? stepIndex : 0;
  }, [steps, currentStepId]);

  const previousStepIndex = useMemo(() => {
    return currentStepIndex - 1 >= 0 ? currentStepIndex - 1 : currentStepIndex;
  }, [currentStepIndex]);
  const nextStepIndex = useMemo(() => {
    return currentStepIndex + 1 < steps.length ? currentStepIndex + 1 : currentStepIndex;
  }, [currentStepIndex, steps.length]);

  const nextStep = useCallback(() => {
    const nextStepId = steps[nextStepIndex]?.id;

    setCurrentStepId(nextStepId);
    navigate(`/premarketplace/${nextStepId}?${searchParams.toString()}`);
  }, [nextStepIndex, steps, navigate, searchParams]);

  const previousStep = useCallback(() => {
    const previousStepId = steps[previousStepIndex]?.id;

    setCurrentStepId(previousStepId);
    navigate(-1);
  }, [previousStepIndex, steps, navigate]);

  const goToStep = useCallback(() => {}, []);

  const resetSteps = useCallback(() => {
    setCurrentStepId(steps[0]?.id);
  }, [steps]);

  return {
    currentStepId,
    currentStepIndex,
    totalSteps: steps.length,
    isFirstStep: currentStepIndex === 0,
    isLastStep: currentStepIndex === steps.length - 1,
    nextStep,
    previousStep,
    goToStep,
    resetSteps,
  };
};

/** StepContext */
export interface StepData {
  [key: string]: unknown;
}

interface StepContextType extends StepManager {
  config: ModalStepConfiguration | null;
  setConfig: (config: ModalStepConfiguration | null) => void;
}

const StepContext = createContext<StepContextType | undefined>(undefined);

interface StepProviderProps {
  children: ReactNode;
  partner?: MarketplacePartner;
}

export const StepProvider = ({ children, partner }: StepProviderProps) => {
  const partnerStepConfig = usePartnerStepConfiguration(partner);
  const [config, setConfig] = useState<ModalStepConfiguration | null>(partnerStepConfig);
  const steps = config?.steps || [];
  const stepManager = useStepManager(steps);

  const setConfigCallback = useCallback((config: ModalStepConfiguration | null) => {
    setConfig(config);
  }, []);

  const contextValue: StepContextType = useMemo(
    () => ({ ...stepManager, config, setConfig: setConfigCallback }),
    [stepManager, config, setConfigCallback],
  );

  return <StepContext.Provider value={contextValue}>{children}</StepContext.Provider>;
};

export const useStepContext = (): StepContextType => {
  const context = useContext(StepContext);
  if (!context) {
    throw new Error('useStepContext must be used within a StepProvider');
  }
  return context;
};
